import { motion } from "framer-motion";
import { QrCode, CloudCheck, DeviceMobile, Printer } from "@phosphor-icons/react";
import { useLang } from "../../context/LangContext";
import { IMAGES } from "../../lib/images";

const icons = [QrCode, CloudCheck, DeviceMobile, Printer];

const reveal = {
  hidden: { opacity: 0, y: 28 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};

export const Features = () => {
  const { t } = useLang();
  return (
    <section id="features" className="bg-[#F8F9FA] py-24 lg:py-32" data-testid="features">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="max-w-2xl">
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="font-mono-tech text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600">
            [ {t.features.overline} ]
          </motion.p>
          <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={reveal} className="mt-4 font-display font-bold text-4xl sm:text-5xl tracking-tight text-[#0F172A]">
            {t.features.title}
          </motion.h2>
          <motion.p initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={reveal} className="mt-5 text-base lg:text-lg leading-relaxed text-slate-600">
            {t.features.subtitle}
          </motion.p>
        </div>

        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden">
          {t.features.items.map((item, i) => {
            const Icon = icons[i];
            return (
              <motion.div
                key={i}
                custom={i}
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                className="group relative bg-white p-8 hover:bg-[#0A0F1A] transition-colors duration-500"
                data-testid={`feature-card-${i}`}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-[4px] bg-emerald-500/10 group-hover:bg-emerald-500 transition-colors duration-500">
                  <Icon size={26} weight="duotone" className="text-emerald-600 group-hover:text-[#04170F] transition-colors duration-500" />
                </div>
                <h3 className="mt-6 font-display font-bold text-xl text-[#0F172A] group-hover:text-white transition-colors duration-500">{item.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600 group-hover:text-slate-300 transition-colors duration-500">{item.d}</p>
                <span className="absolute top-6 right-6 font-mono-tech text-xs text-slate-300 group-hover:text-emerald-500 transition-colors duration-500">0{i + 1}</span>
              </motion.div>
            );
          })}
        </div>

        {/* Split image band */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 grid md:grid-cols-2 gap-8 items-stretch">
          <div className="relative overflow-hidden rounded-md border border-slate-200 min-h-[280px]">
            <img src={IMAGES.nameplate} alt="Machine nameplate" className="absolute inset-0 h-full w-full object-cover" />
          </div>
          <div className="relative overflow-hidden rounded-md border border-slate-200 min-h-[280px]">
            <img src={IMAGES.inspection} alt="On-site inspection" className="absolute inset-0 h-full w-full object-cover" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
